import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Edit3, X, Save, MapPin, Phone, Globe, Mail, Users, Utensils, Music, Building, Accessibility, Sparkles, Check, Camera, Trash2, Plus, ChevronLeft, ChevronRight, Star, Eye, MessageCircle, Clock, Instagram, Facebook, Youtube, Image as ImageIcon } from 'lucide-react';
import { tf } from '../../utils/translateFeature';
import { openDraftPreview } from '../../utils/draftPreview';
import WeeklyHoursEditor from '../../components/WeeklyHoursEditor';

const API = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_FIELDS = [
  ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['tiktok', 'TikTok'],
  ['twitter', 'X (Twitter)'], ['youtube', 'YouTube'],
];

const BusinessProfileSection = ({ business, editing, setEditing, editForm, setEditForm, handleSaveEdit, handlePhotoUpload, handleDeletePhoto, handleReorderPhotos, handleLogoUpload, uploading }) => {
  const { t } = useTranslation();
  const [editingFeatures, setEditingFeatures] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [categories, setCategories] = useState([]);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [customFeatureText, setCustomFeatureText] = useState('');
  const [customEventTypeText, setCustomEventTypeText] = useState('');
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/filters`).then(res => setFilterOptions(res.data)).catch(() => {});
    axios.get(`${API}/api/categories`).then(res => setCategories(res.data.categories || [])).catch(() => {});
  }, []);

  const setSocial = (key, val) => {
    setEditForm(prev => ({ ...prev, socialLinks: { ...(prev.socialLinks || {}), [key]: val } }));
  };

  // Detect custom "Other" values from additionalFeatures
  useEffect(() => {
    const feats = editForm.additionalFeatures || [];
    const standardFeats = ['Pet-Friendly', 'Kid-Friendly', 'Adults Only (18+)', 'Has Special Offers', 'Other'];
    const custom = feats.find(f => !standardFeats.includes(f));
    setCustomFeatureText(custom || '');
    // If there's a custom value but "Other" isn't toggled, add "Other" to show the input
    if (custom && !feats.includes('Other')) {
      setEditForm(prev => ({
        ...prev,
        additionalFeatures: [...(prev.additionalFeatures || []), 'Other']
      }));
    }
  }, [editForm.additionalFeatures?.length]);

  // Detect custom "Other" values from eventTypes
  useEffect(() => {
    const events = editForm.eventTypes || [];
    const knownEvents = filterOptions.eventTypes || [];
    const custom = events.find(e => e && !knownEvents.includes(e));
    if (custom) {
      setCustomEventTypeText(custom);
      // If there's a custom value but "Other" isn't toggled, add "Other" marker
      if (!events.includes('Other')) {
        setEditForm(prev => ({
          ...prev,
          eventTypes: [...(prev.eventTypes || []).filter(e => e !== custom), 'Other'],
          customEventType: custom,
        }));
      }
    } else {
      setCustomEventTypeText(editForm.customEventType || '');
    }
  }, [editForm.eventTypes?.length, filterOptions.eventTypes]);

  const toggleFeatureItem = (field, item) => {
    setEditForm(prev => {
      const current = prev[field] || [];
      if (item === 'Other' && field === 'additionalFeatures') {
        if (current.includes('Other')) {
          // Remove Other and any custom text
          return { ...prev, [field]: current.filter(i => i !== 'Other' && ['Pet-Friendly', 'Kid-Friendly', 'Adults Only (18+)', 'Has Special Offers'].includes(i)) };
        } else {
          return { ...prev, [field]: [...current, 'Other'] };
        }
      }
      return { ...prev, [field]: current.includes(item) ? current.filter(i => i !== item) : [...current, item] };
    });
  };

  const handleCustomFeatureChange = (text) => {
    setCustomFeatureText(text);
    setEditForm(prev => {
      const standard = (prev.additionalFeatures || []).filter(f =>
        ['Pet-Friendly', 'Kid-Friendly', 'Adults Only (18+)', 'Has Special Offers', 'Other'].includes(f)
      );
      if (text.trim()) {
        return { ...prev, additionalFeatures: [...standard, text.trim()] };
      }
      return { ...prev, additionalFeatures: standard };
    });
  };

  const handleSaveFeatures = async () => {
    setSavingFeatures(true);
    try {
      await handleSaveEdit();
      setEditingFeatures(false);
    } finally {
      setSavingFeatures(false);
    }
  };

  const featureSections = [
    { key: 'groupSizes', label: t('groupSizeLabel', 'Group Sizes'), icon: Users, options: filterOptions.groupSizes || [] },
    { key: 'dietaryOptions', label: t('dietaryOptionsLabel', 'Dietary Options'), icon: Utensils, options: filterOptions.dietaryOptions || [] },
    { key: 'eventTypes', label: t('eventTypesLabel', 'Event Types'), icon: Music, options: filterOptions.eventTypes || [] },
    { key: 'venueType', label: t('venueTypeLabel', 'Venue Type'), icon: Building, options: filterOptions.venueTypes || [] },
    { key: 'additionalFeatures', label: t('additionalFeaturesTitle', 'Additional Features'), icon: Sparkles, options: filterOptions.additionalFeatures || [] },
    { key: 'accessibilityFeatures', label: t('accessibilityFacilities', 'Accessibility'), icon: Accessibility, options: filterOptions.accessibilityFeatures || [] },
  ];

  const hasAnyFeatures = featureSections.some(s => (business[s.key] || []).length > 0);

  return (
    <>
      {/* Photo management */}
      <div className="bg-white rounded-xl shadow p-6 mb-4" data-testid="photo-management-section">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">{t('photos', 'Photos')}</h2>
          {business.photos?.length < 5 && (
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-200 disabled:opacity-50"
              data-testid="add-photo-btn">
              {uploading ? <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> {t('addPhoto', 'Add Photo')}</>}
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} data-testid="photo-file-input" />
        </div>
        <p className="text-xs text-gray-400 mb-3">{t('photoOrderHint', 'First photo is your cover image. Use arrows to reorder.')}</p>
        {business.photos && business.photos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {business.photos.map((photo, i) => {
              const isCover = i === 0;
              const moveLeft = () => {
                if (i === 0) return;
                const arr = [...business.photos];
                [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                handleReorderPhotos(arr);
              };
              const moveRight = () => {
                if (i === business.photos.length - 1) return;
                const arr = [...business.photos];
                [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                handleReorderPhotos(arr);
              };
              const setCover = () => {
                if (i === 0) return;
                const arr = [...business.photos];
                const [moved] = arr.splice(i, 1);
                arr.unshift(moved);
                handleReorderPhotos(arr);
              };
              return (
                <div key={photo} className="relative group rounded-xl overflow-hidden border-2 border-transparent" style={isCover ? { borderColor: '#7c3aed' } : {}}>
                  <div className="aspect-[3/4] bg-gray-50">
                    <img src={photo} alt="" className="w-full h-full object-contain" />
                  </div>
                  {/* Cover badge */}
                  {isCover && (
                    <div className="absolute top-1.5 left-1.5 px-2 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded-full flex items-center gap-1" data-testid="cover-badge">
                      <Star className="h-2.5 w-2.5 fill-current" /> Cover
                    </div>
                  )}
                  {/* Controls overlay */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-8 pb-1.5 px-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between">
                    <div className="flex gap-1">
                      <button onClick={moveLeft} disabled={i === 0}
                        className="p-1.5 bg-white/90 rounded-lg text-gray-700 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`move-left-${i}`}>
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={moveRight} disabled={i === business.photos.length - 1}
                        className="p-1.5 bg-white/90 rounded-lg text-gray-700 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`move-right-${i}`}>
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                      {!isCover && (
                        <button onClick={setCover}
                          className="p-1.5 bg-purple-500 rounded-lg text-white hover:bg-purple-600"
                          data-testid={`set-cover-${i}`} title="Set as cover">
                          <Star className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <button onClick={() => handleDeletePhoto(photo)}
                      className="p-1.5 bg-red-500 rounded-lg text-white hover:bg-red-600"
                      data-testid={`delete-photo-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {/* Mobile: always-visible controls */}
                  <div className="sm:hidden absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-6 pb-1.5 px-1.5 flex items-end justify-between">
                    <div className="flex gap-1">
                      <button onClick={moveLeft} disabled={i === 0}
                        className="p-1.5 bg-white/90 rounded-lg text-gray-700 disabled:opacity-30">
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={moveRight} disabled={i === business.photos.length - 1}
                        className="p-1.5 bg-white/90 rounded-lg text-gray-700 disabled:opacity-30">
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                      {!isCover && (
                        <button onClick={setCover} className="p-1.5 bg-purple-500 rounded-lg text-white">
                          <Star className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <button onClick={() => handleDeletePhoto(photo)} className="p-1.5 bg-red-500 rounded-lg text-white">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <Camera className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">{t('noPhotosDesc', 'No photos yet. Add up to 5 photos.')}</p>
            <button onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm text-purple-600 hover:underline" data-testid="upload-first-photo-btn">
              {t('addPhoto', 'Add Photo')}
            </button>
          </div>
        )}
      </div>

      {/* Basic info */}
      <div className="bg-white rounded-xl shadow p-6 mb-4" data-testid="business-profile-section">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">{t('businessInformation', 'Business Information')}</h2>
          {!editing && (
            <button onClick={() => setEditing(true)} className="text-purple-600 hover:text-purple-700" data-testid="edit-profile-btn">
              <Edit3 className="h-5 w-5" />
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('businessName', 'Business Name')}</label>
              <input type="text" value={editForm.businessName || ''} onChange={(e) => setEditForm({...editForm, businessName: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-business-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('description', 'Description')}</label>
              <textarea value={editForm.description || ''} onChange={(e) => setEditForm({...editForm, description: e.target.value})} rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none" data-testid="edit-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('category', 'Category')}</label>
                <input list="biz-categories" type="text" value={editForm.category || ''} onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-category" />
                <datalist id="biz-categories">
                  {categories.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('townCityLabel', 'Town / City')}</label>
                <input type="text" value={editForm.city || ''} onChange={(e) => setEditForm({...editForm, city: e.target.value, town: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-city" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('countryLabel', 'Country')}</label>
                <input type="text" value={editForm.country || ''} onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-country" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('regionLabel2', 'Region / State')}</label>
                <input type="text" value={editForm.region || ''} onChange={(e) => setEditForm({...editForm, region: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-region" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('provinceLabel', 'Province')}</label>
                <input type="text" value={editForm.province || ''} onChange={(e) => setEditForm({...editForm, province: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-province" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('localAreaLabel', 'Local area / District')}</label>
                <input type="text" value={editForm.localArea || ''} onChange={(e) => setEditForm({...editForm, localArea: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-local-area" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('postcodeLabel', 'Postcode / ZIP')}</label>
                <input type="text" value={editForm.postcode || ''} onChange={(e) => setEditForm({...editForm, postcode: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-postcode" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {t('address', 'Address')}</label>
              <input type="text" value={editForm.address || ''} onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {t('phoneNumber', 'Phone')}</label>
                <input type="tel" value={editForm.phone || ''} onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5 text-green-600" /> {t('whatsapp', 'WhatsApp')}</label>
                <input type="tel" value={editForm.whatsapp || ''} onChange={(e) => setEditForm({...editForm, whatsapp: e.target.value})}
                  placeholder="+34 ..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-whatsapp" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {t('email', 'Contact email')}</label>
                <input type="email" value={editForm.email || ''} onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> {t('websiteLabel', 'Website')}</label>
                <input type="url" value={editForm.website || ''} onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid="edit-website" />
              </div>
            </div>

            {/* Social links */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('socialLinks', 'Social links')}</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SOCIAL_FIELDS.map(([key, label]) => (
                  <input key={key} type="url" value={(editForm.socialLinks || {})[key] || ''}
                    onChange={(e) => setSocial(key, e.target.value)} placeholder={label}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" data-testid={`edit-social-${key}`} />
                ))}
              </div>
            </div>

            {/* Logo / profile image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> {t('logo', 'Logo / profile image')}</label>
              <div className="flex items-center gap-3">
                {business.logoUrl ? (
                  <img src={business.logoUrl} alt="logo" className="h-14 w-14 rounded-lg object-cover border border-gray-200" data-testid="business-logo-preview" />
                ) : (
                  <div className="h-14 w-14 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400"><ImageIcon className="h-6 w-6" /></div>
                )}
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload-input" />
                <button type="button" onClick={() => logoInputRef.current?.click()} disabled={uploading}
                  className="px-3 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50 disabled:opacity-50" data-testid="logo-upload-btn">
                  {uploading ? t('uploading', 'Uploading…') : t('uploadLogo', 'Upload logo')}
                </button>
              </div>
            </div>

            {/* Opening days & times */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t('openingHours', 'Opening days & times')}</label>
              <WeeklyHoursEditor hours={editForm.hours} onChange={(h) => setEditForm(prev => ({ ...prev, hours: h }))} testIdPrefix="hours" />
              <p className="text-xs text-gray-400 mt-1">{t('openingHoursHint', 'Leave a day blank or tick "Closed" if you do not open that day.')}</p>
            </div>

            {/* Special / holiday opening hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('specialHours', 'Special / holiday opening hours')}</label>
              <textarea value={editForm.specialHours || ''} onChange={(e) => setEditForm({...editForm, specialHours: e.target.value})} rows={2}
                placeholder={t('specialHoursPlaceholder', 'e.g. Open 10:00–14:00 on public holidays; closed 24–26 Dec')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none" data-testid="edit-special-hours" />
              <p className="text-xs text-gray-400 mt-1">{t('specialHoursVsClosures', 'For one-off closures use "Temporary Closures" below.')}</p>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={handleSaveEdit} className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700" data-testid="save-profile-btn">{t('save', 'Save')}</button>
              <button
                type="button"
                onClick={() => openDraftPreview(business, editForm)}
                className="flex items-center justify-center gap-1 px-3 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50"
                data-testid="preview-draft-btn"
                title={t('previewUnsavedHint', 'Opens a preview of your unsaved changes in a new tab')}
              >
                <Eye className="h-4 w-4" /> {t('previewChanges', 'Preview')}
              </button>
              <button onClick={() => setEditing(false)} className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">{t('cancel', 'Cancel')}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{business.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
              {business.address && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-400" />{business.address}</div>}
              {business.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" />{business.phone}</div>}
              {business.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-gray-400" />{business.email}</div>}
              {business.website && <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-gray-400" /><a href={business.website} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline truncate">{business.website}</a></div>}
            </div>
          </div>
        )}
      </div>

      {/* Features & Services — always available so owners can add details anytime */}
      <div className="bg-white rounded-xl shadow p-6 mb-4" data-testid="features-services-section">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">{t('featuresAmenities', 'Features & Services')}</h2>
          {!editingFeatures ? (
            <button onClick={() => setEditingFeatures(true)} className="text-purple-600 hover:text-purple-700" data-testid="edit-features-btn">
              <Edit3 className="h-5 w-5" />
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleSaveFeatures} disabled={savingFeatures}
                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                data-testid="save-features-btn">
                <Save className="h-3.5 w-3.5" /> {savingFeatures ? '...' : t('save', 'Save')}
              </button>
              <button
                type="button"
                onClick={() => openDraftPreview(business, editForm)}
                className="flex items-center gap-1 px-3 py-1.5 bg-white border border-purple-300 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-50"
                data-testid="preview-draft-features-btn"
              >
                <Eye className="h-3.5 w-3.5" /> {t('previewChanges', 'Preview')}
              </button>
              <button onClick={() => setEditingFeatures(false)}
                className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200">
                {t('cancel', 'Cancel')}
              </button>
            </div>
          )}
        </div>

        {editingFeatures ? (
          <div className="space-y-5">
            {featureSections.map(section => (
              <div key={section.key}>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <section.icon className="h-4 w-4 text-purple-500" />
                  {section.label}
                </label>
                <div className="flex flex-wrap gap-2">
                  {section.options.map(opt => {
                    const selected = (editForm[section.key] || []).includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleFeatureItem(section.key, opt)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selected ? 'bg-purple-100 border-purple-400 text-purple-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                        }`}
                        data-testid={`feature-opt-${section.key}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      >
                        {selected && <Check className="h-3 w-3 inline mr-1" />}
                        {tf(t, opt)}
                      </button>
                    );
                  })}
                </div>
                {/* "Other" text input for additionalFeatures and eventTypes */}
                {section.key === 'additionalFeatures' && (editForm.additionalFeatures || []).includes('Other') && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={customFeatureText}
                      onChange={(e) => handleCustomFeatureChange(e.target.value)}
                      placeholder={t('specifyFeature', 'Describe your feature/service...')}
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none bg-purple-50"
                      data-testid="custom-feature-input"
                    />
                  </div>
                )}
                {section.key === 'eventTypes' && (editForm.eventTypes || []).includes('Other') && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={customEventTypeText}
                      onChange={(e) => {
                        setCustomEventTypeText(e.target.value);
                        setEditForm(prev => ({ ...prev, customEventType: e.target.value }));
                      }}
                      placeholder={t('specifyEventType', 'Describe the event type...')}
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none bg-purple-50"
                      data-testid="custom-event-type-input"
                    />
                  </div>
                )}
              </div>
            ))}
            <p className="text-xs text-gray-400">{t('featureEditHint', 'Click items to add or remove them. Save when done.')}</p>
          </div>
        ) : (
          hasAnyFeatures ? (
            <div className="space-y-3">
              {featureSections.map(section => {
                const items = business[section.key] || [];
                if (items.length === 0) return null;
                return (
                  <div key={section.key}>
                    <p className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                      <section.icon className="h-3.5 w-3.5" /> {section.label}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((item, i) => (
                        <span key={i} className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{tf(t, item)}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-400 mb-2">{t('noFeaturesYet', 'No features or services added yet.')}</p>
              <button onClick={() => setEditingFeatures(true)} className="text-sm text-purple-600 hover:underline" data-testid="add-features-btn">
                {t('addFeatures', 'Add Features & Services')}
              </button>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default BusinessProfileSection;
